/**
 * ActualSpentExportModal Component
 * 
 * Maestro Payroll Export modal with date range filtering (week or custom) and project selection.
 * Exports actual_spent data in Maestro-compatible format with 9-row expansion per entry.
 */

import React, { useState, useEffect } from 'react';
import TimeTrackerAdminHelpModal from './TimeTrackerAdminHelpModal';

// Helper to format date as YYYY-MM-DD using local timezone
const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Add days to a YYYY-MM-DD string and return new YYYY-MM-DD string
const addDaysToDateString = (dateStr, days) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  date.setDate(date.getDate() + days);
  return toLocalDateString(date);
};

// Get week dates as strings from a start date string.
// weekMode: 'week-sun' → Sun–Sat (CAD), 'week-mon' → Mon–Sun (U.S.)
const getWeekDateStrings = (startDateStr, weekMode = 'week-sun') => {
  const [year, month, day] = startDateStr.split('-').map(Number);
  const start = new Date(year, month - 1, day, 12, 0, 0);
  const anchorDay = weekMode === 'week-mon' ? 1 : 0; // 1=Mon, 0=Sun
  const dayOfWeek = start.getDay();
  // How many days back to the anchor day?
  const diff = (dayOfWeek - anchorDay + 7) % 7;
  start.setDate(start.getDate() - diff);

  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    dates.push({
      dateStr: toLocalDateString(date),
      dayOfWeek: date.getDay(),
      dayNum: date.getDate(),
      month: date.getMonth()
    });
  }
  return dates;
};

// Format week title
const formatWeekTitle = (weekDates) => {
  if (weekDates.length < 7) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const start = weekDates[0];
  const end = weekDates[6];
  const year = start.dateStr.split('-')[0];
  return `${months[start.month]} ${start.dayNum} - ${months[end.month]} ${end.dayNum}, ${year}`;
};

const ActualSpentExportModal = ({
  isOpen,
  onClose,
  onExport,
  isExporting,
  exportStatus,
  statusType,
  projects = []
}) => {
  // Date mode: 'week-sun' (CAD, Sun–Sat) | 'week-mon' (U.S., Mon–Sun) | 'custom'
  const [showHelp, setShowHelp] = useState(false);
  const [dateMode, setDateMode] = useState('week-sun');
  
  // Week mode state
  const [weekStartStr, setWeekStartStr] = useState(() => toLocalDateString(new Date()));
  const [weekDates, setWeekDates] = useState([]);
  
  // Custom mode state
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Job number filter
  const [selectedJobNumber, setSelectedJobNumber] = useState('all');

  const isWeekMode = dateMode === 'week-sun' || dateMode === 'week-mon';

  // Recalculate week dates when weekStartStr or dateMode changes
  useEffect(() => {
    if (isWeekMode) {
      setWeekDates(getWeekDateStrings(weekStartStr, dateMode));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartStr, dateMode]);

  // Navigate weeks
  const goToPreviousWeek = () => {
    setWeekStartStr(addDaysToDateString(weekStartStr, -7));
  };

  const goToNextWeek = () => {
    setWeekStartStr(addDaysToDateString(weekStartStr, 7));
  };

  // Get the date range based on mode
  const getDateRange = () => {
    if (isWeekMode && weekDates.length === 7) {
      return { startDate: weekDates[0].dateStr, endDate: weekDates[6].dateStr };
    } else if (dateMode === 'custom' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    return { startDate: null, endDate: null };
  };

  // Handle export click
  const handleExportClick = () => {
    const { startDate, endDate } = getDateRange();
    onExport({
      startDate,
      endDate,
      jobNumber: selectedJobNumber
    });
  };

  // Validate custom date range
  const isCustomRangeValid = () => {
    if (dateMode !== 'custom') return true;
    if (!customStartDate || !customEndDate) return false;
    return customStartDate <= customEndDate;
  };

  // When switching week modes, re-snap the current date to the new anchor
  const handleDateModeChange = (newMode) => {
    setDateMode(newMode);
    if (newMode === 'week-sun' || newMode === 'week-mon') {
      // Re-snap to the correct week boundary for the new mode
      setWeekDates(getWeekDateStrings(weekStartStr, newMode));
    }
  };

  // Can export?
  const canExport = !isExporting && isCustomRangeValid();

  if (!isOpen) return null;

  // Sort projects by job number
  const sortedProjects = [...projects].sort((a, b) => {
    const numA = parseInt(a.job_number, 10);
    const numB = parseInt(b.job_number, 10);
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    return (a.job_number || '').localeCompare(b.job_number || '');
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '500px',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
            Maestro Payroll Export
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => setShowHelp(true)}
            disabled={isExporting}
            style={{
              padding: '6px 10px', backgroundColor: '#0f766e', color: 'white',
              border: 'none', borderRadius: '6px', cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem', fontWeight: 600, opacity: isExporting ? 0.5 : 1,
            }}
          >
            ❓ Help
          </button>
          <button
            onClick={onClose}
            disabled={isExporting}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              color: '#6b7280',
              opacity: isExporting ? 0.5 : 1
            }}
          >
            ×
          </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* Project Selector */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Project #
            </label>
            <select
              value={selectedJobNumber}
              onChange={(e) => setSelectedJobNumber(e.target.value)}
              disabled={isExporting}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.9rem',
                backgroundColor: 'white'
              }}
            >
              <option value="all">All Projects</option>
              {sortedProjects.map(project => (
                <option key={project.id} value={project.job_number}>
                  {project.job_number} - {project.job_name || project.name || 'Unnamed'}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range Mode Selector */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Date Range
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.25rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="dateMode"
                  value="week-sun"
                  checked={dateMode === 'week-sun'}
                  onChange={() => handleDateModeChange('week-sun')}
                  disabled={isExporting}
                />
                <span>Week (Sun–Sat)</span>
                <span style={{
                  fontSize: '0.72rem', fontWeight: '600', letterSpacing: '0.04em',
                  padding: '1px 5px', borderRadius: '4px',
                  backgroundColor: '#dbeafe', color: '#1e40af'
                }}>CAD</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="dateMode"
                  value="week-mon"
                  checked={dateMode === 'week-mon'}
                  onChange={() => handleDateModeChange('week-mon')}
                  disabled={isExporting}
                />
                <span>Week (Mon–Sun)</span>
                <span style={{
                  fontSize: '0.72rem', fontWeight: '600', letterSpacing: '0.04em',
                  padding: '1px 5px', borderRadius: '4px',
                  backgroundColor: '#dcfce7', color: '#166534'
                }}>U.S.</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="dateMode"
                  value="custom"
                  checked={dateMode === 'custom'}
                  onChange={() => handleDateModeChange('custom')}
                  disabled={isExporting}
                />
                <span>Custom Range</span>
              </label>
            </div>
          </div>

          {/* Week Mode UI */}
          {isWeekMode && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              padding: '1rem',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <button
                onClick={goToPreviousWeek}
                disabled={isExporting}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: isExporting ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                &lt; Prev
              </button>
              <span style={{ fontWeight: '600', color: '#374151', minWidth: '180px', textAlign: 'center' }}>
                {formatWeekTitle(weekDates)}
              </span>
              <button
                onClick={goToNextWeek}
                disabled={isExporting}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: isExporting ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Next &gt;
              </button>
            </div>
          )}

          {/* Custom Mode UI */}
          {dateMode === 'custom' && (
            <div style={{
              display: 'flex',
              gap: '1rem',
              padding: '1rem',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  disabled={isExporting}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.9rem'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  disabled={isExporting}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.9rem'
                  }}
                />
              </div>
            </div>
          )}

          {/* Custom range validation error */}
          {dateMode === 'custom' && customStartDate && customEndDate && customStartDate > customEndDate && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              borderRadius: '6px',
              marginBottom: '1rem',
              fontSize: '0.85rem'
            }}>
              End date must be on or after start date
            </div>
          )}

          {/* Status Message */}
          {exportStatus && (
            <div style={{
              padding: '0.75rem 1rem',
              borderRadius: '6px',
              marginBottom: '1rem',
              backgroundColor: statusType === 'success' ? '#d1fae5' : statusType === 'error' ? '#fee2e2' : '#dbeafe',
              color: statusType === 'success' ? '#065f46' : statusType === 'error' ? '#991b1b' : '#1e40af',
              fontSize: '0.9rem'
            }}>
              {exportStatus}
            </div>
          )}

          {/* Summary */}
          <div style={{
            padding: '0.75rem 1rem',
            backgroundColor: '#f0f9ff',
            borderRadius: '6px',
            fontSize: '0.85rem',
            color: '#0369a1'
          }}>
            <strong>Maestro Payroll Export Summary:</strong>
            <br />
            Project: {selectedJobNumber === 'all' ? 'All Projects' : selectedJobNumber}
            <br />
            Dates: {isWeekMode && weekDates.length === 7
              ? `${weekDates[0].dateStr} to ${weekDates[6].dateStr} (${dateMode === 'week-mon' ? 'U.S. Mon–Sun' : 'CAD Sun–Sat'})`
              : dateMode === 'custom' && customStartDate && customEndDate
                ? `${customStartDate} to ${customEndDate}`
                : 'Select dates'}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem',
          padding: '1rem 1.5rem',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <button
            onClick={onClose}
            disabled={isExporting}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#374151',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              opacity: isExporting ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleExportClick}
            disabled={!canExport}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: canExport ? '#17a2b8' : '#9ca3af',
              color: 'white',
              cursor: canExport ? 'pointer' : 'not-allowed',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {isExporting && (
              <span style={{
                display: 'inline-block',
                width: '14px',
                height: '14px',
                border: '2px solid white',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
            {isExporting ? 'Exporting...' : 'Export Maestro Payroll'}
          </button>
        </div>
      </div>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      {showHelp && <TimeTrackerAdminHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
};

export default ActualSpentExportModal;
